import React, { useState, useEffect } from "react";
import { FaEnvelope, FaMapMarkerAlt, FaPhone, FaCheckCircle, FaPaperPlane } from "react-icons/fa";
import { Helmet } from "react-helmet-async";
import "./Pages.css";

const Contact = () => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    message: "",
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const formDataToSend = new FormData();
    formDataToSend.append("name", formData.name);
    formDataToSend.append("email", formData.email);
    formDataToSend.append("phone", formData.phone);
    formDataToSend.append("company", formData.company);
    formDataToSend.append("message", formData.message);

    try {
      const response = await fetch("https://formspree.io/f/mpqjboey", {
        method: "POST",
        body: formDataToSend,
        headers: { Accept: "application/json" },
      });

      if (response.ok) {
        setIsSubmitted(true);
        setFormData({ name: "", email: "", phone: "", company: "", message: "" });
        setTimeout(() => setIsSubmitted(false), 5000);
      } else {
        alert("There was an error submitting the form. Please try again.");
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      alert("There was an error submitting the form. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main>
      <Helmet>
        <title>Contact Us | ThinkRise USA</title>
        <meta
          name="description"
          content="Contact ThinkRise USA to discuss your goals. Get help with verified B2B data, demand generation, revenue operations, and automation for predictable pipeline."
        />
      </Helmet>
      <section className="page-hero contact-hero">
        <div className="container">
          <h1 className="page-hero-title">Contact ThinkRise USA</h1>
          <p className="page-hero-subtitle">
            Share your goals and we will help you build a clear, measurable growth plan.
          </p>
          <a href="#contact-form" className="btn">Start Conversation</a>
        </div>
      </section>

      <section className="section contact-section">
        <div className="container">
          <div className="contact-content">
            <div className="contact-form-container">
              <h2 className="section-title" id="contact-form">Send a Message</h2>

              {isSubmitted ? (
                <div className="success-message">
                  <FaCheckCircle className="success-icon" />
                  <h3>Thank you</h3>
                  <p>Your message has been delivered. Our team will respond shortly.</p>
                  <button className="btn" onClick={() => setIsSubmitted(false)} style={{ marginTop: "20px" }}>
                    Send Another Message
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="contact-form">
                  <input type="hidden" name="_subject" value="New Contact Form Submission - ThinkRise USA" />
                  <input type="hidden" name="_replyto" value={formData.email} />
                  <input type="text" name="_gotcha" style={{ display: "none" }} />

                  <div className="form-group">
                    <label htmlFor="name">Name *</label>
                    <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} required placeholder="Your Name" disabled={isSubmitting} />
                  </div>

                  <div className="form-group">
                    <label htmlFor="email">Email *</label>
                    <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} required placeholder="example@gmail.com" disabled={isSubmitting} />
                  </div>

                  <div className="form-group">
                    <label htmlFor="phone">Phone</label>
                    <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} placeholder="1(234)-567-8901" disabled={isSubmitting} />
                  </div>

                  <div className="form-group">
                    <label htmlFor="company">Company</label>
                    <input type="text" id="company" name="company" value={formData.company} onChange={handleChange} placeholder="Company Name" disabled={isSubmitting} />
                  </div>

                  <div className="form-group">
                    <label htmlFor="message">Message *</label>
                    <textarea id="message" name="message" value={formData.message} onChange={handleChange} required placeholder="Tell us about your goals..." rows="6" disabled={isSubmitting}></textarea>
                  </div>

                  <button type="submit" className="btn submit-btn" disabled={isSubmitting}>
                    {isSubmitting ? (
                      <>
                        <span className="spinner"></span> Sending...
                      </>
                    ) : (
                      <>
                        <FaPaperPlane style={{ marginRight: "8px" }} /> Send Message
                      </>
                    )}
                  </button>

                  <p className="privacy-note">We keep your information private and secure.</p>
                </form>
              )}
            </div>

            <div className="contact-info-container">
              <h2 className="section-title">Direct Contact</h2>
              <p className="contact-info-intro">Prefer direct communication? Reach us here.</p>

              <div className="contact-details">
                <div className="contact-detail">
                  <div className="contact-icon-wrapper"><FaEnvelope className="contact-detail-icon" /></div>
                  <div className="contact-detail-content">
                    <h3>Email</h3>
                    <a href="mailto:Info@thinkriseusa.com" className="contact-link">Info@thinkriseusa.com</a>
                  </div>
                </div>

                <div className="contact-detail">
                  <div className="contact-icon-wrapper"><FaPhone className="contact-detail-icon" /></div>
                  <div className="contact-detail-content">
                    <h3>Phone</h3>
                    <a href="tel:+14693517616" className="contact-link">1(469)-351-7616</a>
                  </div>
                </div>

                <div className="contact-detail">
                  <div className="contact-icon-wrapper"><FaMapMarkerAlt className="contact-detail-icon" /></div>
                  <div className="contact-detail-content">
                    <h3>Address</h3>
                    <address className="contact-address">Frisco, TX 75034</address>
                  </div>
                </div>
              </div>

              <div className="response-time">
                <h3>Response Time</h3>
                <p>Typical reply window is within one business day.</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Contact;
